import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getVendorBillDetails } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import * as routes from '../../../Routing/routes';

const ViewVendorBill = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isPrint = searchParams.get('print') === 'true';

    const { vendorBillDetailsData, vendorBillDetailsLoading } = useSelector(state => state.VendorReducers);
    console.log('vendorBillDetailsData',vendorBillDetailsData)
    useEffect(() => {
        dispatch(getVendorBillDetails({ billId: id }));
    }, [dispatch, id]);

    // Auto-trigger print
    useEffect(() => {
        if (isPrint && vendorBillDetailsData && !vendorBillDetailsLoading) {
            const timer = setTimeout(() => window.print(), 500);
            return () => clearTimeout(timer);
        }
    }, [isPrint, vendorBillDetailsData, vendorBillDetailsLoading]);

    const billData = useMemo(() => {
        if (!vendorBillDetailsData) return null;
        const d = vendorBillDetailsData;

        // Calculate GST from details if not at top level
        const details = d.details || [];
        let totalCgst = 0, totalSgst = 0, totalIgst = 0;
        details.forEach(item => {
            const base = parseFloat(item.amount || 0);
            totalCgst += base * parseFloat(item.cgst_rate || 0) / 100;
            totalSgst += base * parseFloat(item.sgst_rate || 0) / 100;
            totalIgst += base * parseFloat(item.igst_rate || 0) / 100;
        });

        return {
            id: d.bill_id || d.id,
            billNo: d.bill_number || '',
            billType: d.bill_type || '',
            vendor: d.vendor?.business_name || d.vendor_name || 'N/A',
            vendorCode: d.vendor?.vendor_code || '',
            vendorGst: d.vendor?.gst_number || '',
            vendorPan: d.vendor?.pan_number || '',
            category: d.category || '',
            description: d.description || '',
            billDate: d.bill_date,
            dueDate: d.due_date,
            status: d.status || 'Draft',
            rcmApplicable: d.rcm_applicable || 'N',
            tdsApplicable: d.tds_applicable || 'N',
            ldcApplicable: d.ldc_applicable || 'N',
            notes: d.notes || '',
            remarks: d.remarks || '',
            society: d.society || {},
            // Details (tariff items)
            details: details,
            // Amounts
            subtotal: parseFloat(d.amount || 0),
            taxAmount: parseFloat(d.tax_amount || 0),
            cgstAmount: totalCgst,
            sgstAmount: totalSgst,
            igstAmount: totalIgst,
            tdsAmount: parseFloat(d.tds_amount || 0),
            ldcTds: parseFloat(d.ldc_tds || 0),
            totalAmount: parseFloat(d.total_amount || d.total || 0),
            netAmount: parseFloat(d.netamount || d.net_amount || 0),
            paidAmount: parseFloat(d.paid_amount || 0),
            balanceAmount: parseFloat(d.balance_amount || 0),
            // RCM
            rcmCgst: parseFloat(d.rcm_cgst || 0),
            rcmSgst: parseFloat(d.rcm_sgst || 0),
            rcmIgst: parseFloat(d.rcm_igst || 0),
        };
    }, [vendorBillDetailsData]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const numberToWords = (num) => {
        if (num === 0) return 'Zero';
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
        };
        const absNum = Math.abs(Math.round(num));
        if (absNum === 0) return 'Zero';
        let result = '';
        const crore = Math.floor(absNum / 10000000);
        const lakh = Math.floor((absNum % 10000000) / 100000);
        const thousand = Math.floor((absNum % 100000) / 1000);
        const remainder = absNum % 1000;
        if (crore) result += convertLessThanThousand(crore) + ' Crore ';
        if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
        if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
        if (remainder) result += convertLessThanThousand(remainder);
        return (num < 0 ? 'Minus ' : '') + result.trim() + ' Rupees Only';
    };

    const getStatusStyles = (status) => {
        if (status?.toLowerCase() === 'draft') return { background: '#fef3c7', color: '#92400e' };
        return { background: '#d1fae5', color: '#065f46' };
    };

    const getStatusLabel = (status) => {
        return status?.toLowerCase() === 'draft' ? 'DRAFT' : 'POST';
    };

    const handlePrint = () => window.print();
    const handleBack = () => navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS);

    if (vendorBillDetailsLoading) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-indigo-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">Loading bill details...</p>
                </div>
            </div>
        );
    }

    if (!billData) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                    <Icon icon="mdi:file-document-alert" className="tw-text-4xl tw-text-red-400" />
                    <p className="tw-text-gray-600 tw-font-medium">Bill not found</p>
                    <button onClick={handleBack} className="tw-text-blue-600 hover:tw-underline tw-text-sm">
                        Back to Bills
                    </button>
                </div>
            </div>
        );
    }

    const tariffItems = Array.isArray(billData.details) ? billData.details : [];
    const statusStyles = getStatusStyles(billData.status);
    const hasRcm = billData.rcmApplicable === 'Y';
    const hasTds = billData.tdsAmount > 0;
    const society = billData.society || {};
    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');
    const primaryContact = society.contacts?.find(c => c.is_primary === 1) || society.contacts?.[0] || {};

    return (
        <>
            {/* Print-specific styles */}
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    .print-page { margin: 0; padding: 6mm; box-shadow: none !important; border: none !important; }
                    @page { size: A4; margin: 8mm; }
                }
            `}</style>

            {/* Action Bar - hidden in print */}
            <div className="no-print tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
                <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={handleBack} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500 tw-transition-colors">
                            <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                        </button>
                        <div>
                            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Vendor Bill</h1>
                            <p className="tw-text-xs tw-text-gray-500">{billData.billNo}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            onClick={handleBack}
                            className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
                        >
                            Back
                        </button>
                        <button
                            onClick={handlePrint}
                            className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-semibold hover:tw-bg-indigo-700 tw-transition-colors tw-shadow-sm"
                        >
                            <Icon icon="mdi:printer" className="tw-text-lg" /> Print
                        </button>
                    </div>
                </div>
            </div>

            {/* Print Layout */}
            <div className="tw-bg-gray-100 tw-min-h-screen tw-py-8 no-print-bg">
                <div className="print-page tw-max-w-4xl tw-mx-auto tw-bg-white tw-shadow-lg tw-border tw-border-gray-200" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", fontSize: '12px' }}>

                    {/* Header - Society Info + Bill Number */}
                    <div style={{ borderBottom: '3px solid #4338ca', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ maxWidth: '60%' }}>
                            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1e1b4b', margin: 0, letterSpacing: '-0.3px' }}>
                                Vendor Bill
                            </h1>
                            {society.name && (
                                <p style={{ fontSize: '14px', color: '#4338ca', fontWeight: 700, margin: '4px 0 0 0' }}>
                                    {society.name}
                                </p>
                            )}
                            {societyAddress && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                                    {societyAddress}
                                </p>
                            )}
                            {society.registration_number && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                                    Reg. No: {society.registration_number}
                                </p>
                            )}
                            {(primaryContact.phone || primaryContact.mobile) && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                                    Tel: {primaryContact.phone || ''}{primaryContact.phone && primaryContact.mobile ? ' | ' : ''}{primaryContact.mobile || ''}
                                    {primaryContact.email ? ` | ${primaryContact.email}` : ''}
                                </p>
                            )}
                            {billData.billType && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '4px 0 0 0', textTransform: 'capitalize' }}>
                                    Type: {billData.billType.replace(/_/g, ' ')}
                                </p>
                            )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Bill No.</div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e1b4b', margin: '2px 0' }}>{billData.billNo}</div>
                            <div style={{
                                display: 'inline-block',
                                padding: '3px 12px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                ...statusStyles
                            }}>
                                {getStatusLabel(billData.status)}
                            </div>
                        </div>
                    </div>

                    {/* Date Row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Bill Date</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(billData.billDate)}</div>
                        </div>
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Due Date</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(billData.dueDate)}</div>
                        </div>
                        <div style={{ flex: 1, padding: '10px 28px' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Status</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{getStatusLabel(billData.status)}</div>
                        </div>
                    </div>

                    {/* Vendor Info */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Vendor Details</div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{billData.vendor}</div>
                            {billData.category && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>Category: {billData.category}</div>
                            )}
                            {billData.vendorGst && (
                                <div style={{ fontSize: '10px', color: '#4338ca', marginTop: '2px', fontWeight: 600 }}>GSTIN: {billData.vendorGst}</div>
                            )}
                            {billData.vendorPan && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>PAN: {billData.vendorPan}</div>
                            )}
                            {billData.vendorCode && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Code: {billData.vendorCode}</div>
                            )}
                        </div>
                        <div style={{ flex: 1, padding: '14px 28px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Description</div>
                            {/* Authored as rich text. Rendered as markup here, the same way
                                notices and tasks render their bodies, so the tags don't show. */}
                            {billData.description
                                ? <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: billData.description }} />
                                : <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>-</div>}
                            <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {hasRcm && (
                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                                        RCM Applicable
                                    </span>
                                )}
                                {billData.tdsApplicable === 'Y' && (
                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: 600, background: '#f3e8ff', color: '#7c3aed' }}>
                                        TDS Applicable
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tariff Items Table */}
                    <div style={{ padding: '0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    <th style={{ textAlign: 'left', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '30px' }}>#</th>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Item / Head</th>
                                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '50px' }}>Qty</th>
                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '90px' }}>Rate</th>
                                    <th style={{ textAlign: 'right', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '100px' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Filter out tax-related heads
                                    const taxHeads = ['SGST', 'CGST', 'IGST', 'TDS', 'RCM', 'ROUND OFF'];
                                    const filteredItems = tariffItems.filter(item => {
                                        const headName = (item.item_name || item.description || '').toUpperCase();
                                        return !taxHeads.some(taxHead => headName.includes(taxHead));
                                    });

                                    return filteredItems.map((item, idx) => {
                                        const headName = item.item_name || item.description || '-';
                                        const qty = parseFloat(item.quantity || item.qty || 1);
                                        const rate = parseFloat(item.rate || 0);
                                        const amount = parseFloat(item.amount || 0);
                                        const tdsRate = parseFloat(item.tds_rate || 0);

                                        return (
                                            <tr key={item.bill_detail_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 28px', color: '#9ca3af' }}>{idx + 1}</td>
                                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1f2937' }}>
                                                    {headName}
                                                    {item.description && item.item_name && item.description !== item.item_name && (
                                                        <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 400, marginTop: '1px' }}>{item.description}</div>
                                                    )}
                                                    {item.tds_section && (
                                                        <div style={{ fontSize: '9px', color: '#7c3aed', fontWeight: 500, marginTop: '1px' }}>
                                                            TDS: {tdsRate}% (Sec {item.tds_section})
                                                        </div>
                                                    )}
                                                    {item.glcode && (
                                                        <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 400, marginTop: '1px' }}>GL: {item.glcode}</div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', color: '#374151' }}>{qty}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>{formatCurrency(rate)}</td>
                                                <td style={{ padding: '8px 28px', textAlign: 'right', fontWeight: 700, color: '#111827' }}>{formatCurrency(amount)}</td>
                                            </tr>
                                        );
                                    });
                                })()}
                                {tariffItems.filter(item => {
                                    const headName = (item.item_name || item.description || '').toUpperCase();
                                    const taxHeads = ['SGST', 'CGST', 'IGST', 'TDS', 'RCM', 'ROUND OFF'];
                                    return !taxHeads.some(taxHead => headName.includes(taxHead));
                                }).length === 0 && (
                                    <tr>
                                        <td colSpan={5} style={{ padding: '20px 28px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                                            No tariff items
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary Section */}
                    <div style={{ borderTop: '2px solid #e2e8f0', display: 'flex' }}>
                        {/* Left: Amount in words + Notes */}
                        <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Amount in Words</div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', fontStyle: 'italic' }}>
                                {numberToWords(billData.netAmount || billData.totalAmount)}
                            </div>

                            {billData.remarks && (
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Remarks</div>
                                    <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>{billData.remarks}</div>
                                </div>
                            )}

                            {billData.notes && (
                                <div style={{ marginTop: '8px' }}>
                                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Notes</div>
                                    <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>{billData.notes}</div>
                                </div>
                            )}

                            {/* Signature area */}
                            <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ borderTop: '1px solid #d1d5db', width: '150px', paddingTop: '6px' }}>
                                        <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>Prepared By</span>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ borderTop: '1px solid #d1d5db', width: '150px', paddingTop: '6px' }}>
                                        <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>Authorized Signatory</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Summary breakdown */}
                        <div style={{ width: '260px', padding: '14px 28px' }}>
                            {/* Subtotal */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                <span style={{ color: '#6b7280' }}>Subtotal</span>
                                <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.subtotal)}</span>
                            </div>

                            {/* Normal GST (when RCM is N) */}
                            {!hasRcm && (
                                <>
                                    {billData.cgstAmount > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280' }}>CGST</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.cgstAmount)}</span>
                                        </div>
                                    )}
                                    {billData.sgstAmount > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280' }}>SGST</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.sgstAmount)}</span>
                                        </div>
                                    )}
                                    {billData.igstAmount > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280' }}>IGST</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.igstAmount)}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* RCM GST */}
                            {hasRcm && (
                                <>
                                    <div style={{ fontSize: '9px', color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 0 2px 0', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>RCM Payable</div>
                                    {billData.rcmCgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>CGST (Payable)</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.rcmCgst)}</span>
                                        </div>
                                    )}
                                    {billData.rcmSgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>SGST (Payable)</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.rcmSgst)}</span>
                                        </div>
                                    )}
                                    {billData.rcmIgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>IGST (Payable)</span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(billData.rcmIgst)}</span>
                                        </div>
                                    )}
                                    <div style={{ fontSize: '9px', color: '#059669', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 0 2px 0' }}>RCM Receivable</div>
                                    {billData.rcmCgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>CGST (Receivable)</span>
                                            <span style={{ fontWeight: 600, color: '#dc2626' }}>-{formatCurrency(billData.rcmCgst)}</span>
                                        </div>
                                    )}
                                    {billData.rcmSgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>SGST (Receivable)</span>
                                            <span style={{ fontWeight: 600, color: '#dc2626' }}>-{formatCurrency(billData.rcmSgst)}</span>
                                        </div>
                                    )}
                                    {billData.rcmIgst > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280', paddingLeft: '8px' }}>IGST (Receivable)</span>
                                            <span style={{ fontWeight: 600, color: '#dc2626' }}>-{formatCurrency(billData.rcmIgst)}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Total (with tax) */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px 0', fontSize: '12px', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>
                                <span style={{ fontWeight: 700, color: '#1f2937' }}>Total</span>
                                <span style={{ fontWeight: 700, color: '#1f2937' }}>{formatCurrency(billData.totalAmount)}</span>
                            </div>

                            {/* TDS */}
                            {hasTds && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                    <span style={{ color: '#7c3aed' }}>TDS Deduction</span>
                                    <span style={{ fontWeight: 600, color: '#dc2626' }}>-{formatCurrency(billData.tdsAmount)}</span>
                                </div>
                            )}

                            {/* Net Payable */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px 0', fontSize: '13px', borderTop: '2px solid #1e1b4b', marginTop: '6px' }}>
                                <span style={{ fontWeight: 700, color: '#1e1b4b' }}>Net Payable</span>
                                <span style={{ fontWeight: 800, color: '#1e1b4b' }}>Rs {formatCurrency(billData.netAmount || billData.totalAmount)}</span>
                            </div>

                            {/* Paid & Balance */}
                            {billData.paidAmount > 0 && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                        <span style={{ color: '#059669' }}>Amount Paid</span>
                                        <span style={{ fontWeight: 600, color: '#059669' }}>- {formatCurrency(billData.paidAmount)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>
                                        <span style={{ fontWeight: 700, color: billData.balanceAmount > 0 ? '#dc2626' : '#059669' }}>Balance Due</span>
                                        <span style={{ fontWeight: 800, color: billData.balanceAmount > 0 ? '#dc2626' : '#059669' }}>Rs {formatCurrency(billData.balanceAmount)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                        <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                            Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                            {society.name ? `${society.name} | ` : ''}Vendor Bill | {billData.billNo}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewVendorBill;
